import {
  type CSSProperties,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  clearDesktopPanelLayout,
  type DesktopPanelLayout,
  type DesktopPanelLayoutKey,
  loadDesktopPanelLayout,
  saveDesktopPanelLayout,
} from "@/shared/lib/desktopPanelLayout";

interface PanelPosition {
  left: number;
  top: number;
}

export interface PanelSize {
  width: number;
  height: number;
}

export interface PanelSizeConstraints {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
}

/** サイズ変更を開始できるパネルの隅。右上は閉じるボタンがあるため持たない。 */
export type PanelResizeCorner = "top-left" | "bottom-left" | "bottom-right";

/** パネルの位置と寸法をまとめたもの。 */
export interface PanelRect extends PanelPosition, PanelSize {}

export interface DragHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

export type ResizeHandleProps = DragHandleProps & {
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
};

export interface UseDraggablePanelOptions {
  /**
   * 呼び出し元が管理する折りたたみ状態。resize や折りたたみトグルのたびに、
   * この値を踏まえてタブ (またはタイトルバー) が画面内に残るよう再クランプする。
   */
  collapsed?: boolean;
  /** 指定するとサイズと位置をブラウザーへ保存する。省略時は保存しない。 */
  persistKey?: DesktopPanelLayoutKey;
  /**
   * 保存レイアウト復元時と、CSSから最小寸法を読めない場合に使う最小寸法。
   * 省略時はフォームパネル向けの既定値 (320x288) を使う。フォームより小さく
   * できるパネル (ミニプレイヤー等) は自身のCSS最小寸法と揃えた値を渡す。
   */
  fallbackMinSize?: PanelSize;
  /** trueの場合はpersistKeyの変更時に、変更後の保存レイアウトを復元する。 */
  restoreOnPersistKeyChange?: boolean;
  /** 展開欄などの一時的な高さ。表示へ加算し、サイズ保存からは除外する。 */
  transientHeight?: number;
}

interface UseDraggablePanelResult {
  panelRef: RefObject<HTMLElement | null>;
  /** 移動後の位置とサイズ変更後の寸法をパネルへ適用する */
  panelStyle: CSSProperties | undefined;
  /** タイトルバーなどドラッグハンドルへ渡す */
  handleProps: DragHandleProps;
  /** パネル各隅のサイズ変更ハンドルへ渡す */
  resizeHandleProps: Record<PanelResizeCorner, ResizeHandleProps>;
  /**
   * パネル位置を水平差分だけ移動する。呼び出し元が幅を外部要因(最小化
   * トグル等)で変えるときに、反対側の辺を固定するために使う。位置未確定
   * (ドラッグ・リサイズ前でCSSのアンカーに任せている間)は何もしない。
   * クランプは行わないため、幅の縮小/復元と対で使い、掴める領域が画面内に
   * 残る範囲の差分だけを渡すこと。
   */
  shiftPanelX: (deltaX: number) => void;
}

/** 掴み直せるだけの領域 (ハンドル約40px) は必ず画面内に残す */
const GRAB_MIN_VISIBLE_PX = 40;
const RESIZE_KEYBOARD_STEP_PX = 16;
const RESIZE_KEYBOARD_LARGE_STEP_PX = 48;
const FALLBACK_MIN_PANEL_WIDTH_PX = 320;
const FALLBACK_MIN_PANEL_HEIGHT_PX = 288;

/**
 * .desktop-floating-tab の CSS 幅 (index.css) と同期させる値。
 * 折りたたみ時の `transform: translateX(calc(100% - 24px))` が動かす
 * 「タブだけを画面に残す」ロジックと対にして使う。CSS 側の幅を変えたら
 * ここも合わせて変更すること。
 */
export const COLLAPSED_TAB_WIDTH_PX = 24;

interface HorizontalBounds {
  min: number;
  max: number;
}

/** パネルのサイズをCSSの最小寸法と現在の表示領域へ収める。 */
export function clampPanelSize(
  size: PanelSize,
  constraints: PanelSizeConstraints,
): PanelSize {
  const maxWidth = Math.max(constraints.maxWidth, 0);
  const maxHeight = Math.max(constraints.maxHeight, 0);
  const minWidth = Math.min(constraints.minWidth, maxWidth);
  const minHeight = Math.min(constraints.minHeight, maxHeight);

  return {
    width: Math.min(Math.max(size.width, minWidth), maxWidth),
    height: Math.min(Math.max(size.height, minHeight), maxHeight),
  };
}

interface PanelResizeCornerVector {
  widthSign: 1 | -1;
  heightSign: 1 | -1;
  movesLeft: boolean;
  movesTop: boolean;
}

/**
 * 隅ごとの伸長方向と、位置を動かすかどうかを返す。
 *
 * ドラッグ量は「掴んだ隅がどちらへ動いたか」なので、左側の隅では
 * ポインターを左へ動かすと幅が増える (widthSign = -1)。位置を動かす側
 * (movesLeft / movesTop) は、対角にある辺を固定するために left / top を
 * 逆算する必要がある隅を示す。
 */
export function getResizeCornerVector(
  corner: PanelResizeCorner,
): PanelResizeCornerVector {
  switch (corner) {
    case "top-left":
      return { widthSign: -1, heightSign: -1, movesLeft: true, movesTop: true };
    case "bottom-left":
      return { widthSign: -1, heightSign: 1, movesLeft: true, movesTop: false };
    case "bottom-right":
      return { widthSign: 1, heightSign: 1, movesLeft: false, movesTop: false };
  }
}

/**
 * 固定される辺を基準にした最大サイズを求める。
 *
 * 右下を掴んだときは左上が固定なので画面右下まで、左側の隅を掴んだときは
 * 右端が固定なので画面左端 (left = 0) まで伸ばせる。この定義により
 * クランプ後の left / top が負にならず、タイトルバーが画面外へ出て
 * 掴めなくなる状態が構造的に起きない。
 */
export function getResizeMaxBounds(
  corner: PanelResizeCorner,
  baseRect: PanelRect,
  viewportWidth: number,
  viewportHeight: number,
): { maxWidth: number; maxHeight: number } {
  const { movesLeft, movesTop } = getResizeCornerVector(corner);
  return {
    maxWidth: movesLeft
      ? baseRect.left + baseRect.width
      : viewportWidth - baseRect.left,
    maxHeight: movesTop
      ? baseRect.top + baseRect.height
      : viewportHeight - baseRect.top,
  };
}

/**
 * ドラッグ量から新しい位置と寸法を求める。ポインターとキーボードの
 * 両方がこの関数を通る。
 *
 * 先に `clampPanelSize` でサイズを確定させ、そのあとに固定辺の座標から
 * left / top を逆算するのが要点。この順序なら幅が minWidth で止まったとき
 * left も `anchorRight - minWidth` で止まるため、`left + width` (固定辺) が
 * 常に一定に保たれ、最小・最大でクランプされても位置が滑らない。
 */
export function resolveResizedRect(
  corner: PanelResizeCorner,
  baseRect: PanelRect,
  deltaX: number,
  deltaY: number,
  constraints: PanelSizeConstraints,
): PanelRect {
  const { widthSign, heightSign, movesLeft, movesTop } =
    getResizeCornerVector(corner);
  const anchorRight = baseRect.left + baseRect.width;
  const anchorBottom = baseRect.top + baseRect.height;

  const { width, height } = clampPanelSize(
    {
      width: baseRect.width + widthSign * deltaX,
      height: baseRect.height + heightSign * deltaY,
    },
    constraints,
  );

  return {
    width,
    height,
    left: movesLeft ? anchorRight - width : baseRect.left,
    top: movesTop ? anchorBottom - height : baseRect.top,
  };
}

/**
 * 矢印キーを、掴んだ隅を動かすポインター等価の移動量へ変換する。
 * 同じキーでも隅によって拡大にも縮小にもなるのは、ドラッグと同じく
 * 「その隅がその方向へ動く」意味だから。対象外のキーは null を返す。
 */
export function resolveResizeKeyboardDelta(
  key: string,
  shiftKey: boolean,
): { deltaX: number; deltaY: number } | null {
  const step = shiftKey
    ? RESIZE_KEYBOARD_LARGE_STEP_PX
    : RESIZE_KEYBOARD_STEP_PX;
  switch (key) {
    case "ArrowLeft":
      return { deltaX: -step, deltaY: 0 };
    case "ArrowRight":
      return { deltaX: step, deltaY: 0 };
    case "ArrowUp":
      return { deltaX: 0, deltaY: -step };
    case "ArrowDown":
      return { deltaX: 0, deltaY: step };
    default:
      return null;
  }
}

/**
 * left の許容範囲を求める。
 *
 * 非折りたたみ時: パネルは常に [tab(24px)][content(301px)] の flex row
 * そのままの見た目で、パネル先頭 (offset 0) にタブがある。パネル先頭の
 * GRAB_MIN_VISIBLE_PX が画面内に残っていれば、タブ (またはタイトルバー)
 * を掴み直せる。
 *
 * 折りたたみ時: CSS の `transform: translateX(calc(100% - 24px))` により
 * パネル全体が右へ `panelWidth - tabWidth` px 移動し、見た目上はパネル
 * "末尾" の tabWidth px (タブ) だけが画面に残る仕組み (index.css
 * `.desktop-floating-panel[data-collapsed="true"]`)。よって折りたたみ時は
 * 先頭基準ではなく「パネル末尾からタブ幅ぶん」を基準にクランプしないと、
 * left が画面右端付近 (viewportWidth - GRAB_MIN_VISIBLE_PX 付近) のとき
 * タブが画面外へ出て掴めなくなる (Issue #151 のレビュー指摘)。
 */
function getHorizontalBounds(
  panelWidth: number,
  viewportWidth: number,
  collapsed: boolean,
  tabWidth: number,
): HorizontalBounds {
  if (collapsed) {
    return {
      min: tabWidth - panelWidth,
      max: viewportWidth - panelWidth,
    };
  }
  return {
    min: GRAB_MIN_VISIBLE_PX - panelWidth,
    max: viewportWidth - GRAB_MIN_VISIBLE_PX,
  };
}

/**
 * ドラッグ後のパネル位置を画面内へ収める。
 *
 * `collapsed` は折りたたみUIを持つ呼び出し元 (DesktopReplyPanel)
 * が管理する状態をそのまま渡す。開閉トグルのタブが画面外へ出て
 * 掴めなくなる不具合 (Issue #151) を防ぐため、折りたたみ中はタブの実効
 * 位置 (パネル末尾から tabWidth ぶん) を基準にクランプする。
 */
export function clampPanelPosition(
  left: number,
  top: number,
  panelWidth: number,
  viewportWidth: number,
  viewportHeight: number,
  collapsed: boolean = false,
  tabWidth: number = COLLAPSED_TAB_WIDTH_PX,
): PanelPosition {
  const { min, max } = getHorizontalBounds(
    panelWidth,
    viewportWidth,
    collapsed,
    tabWidth,
  );
  return {
    left: Math.min(Math.max(left, min), max),
    top: Math.min(Math.max(top, 0), viewportHeight - GRAB_MIN_VISIBLE_PX),
  };
}

function positionsEqual(a: PanelPosition, b: PanelPosition): boolean {
  return a.left === b.left && a.top === b.top;
}

/**
 * resize / 折りたたみトグルの再クランプ経路が共有する更新ロジック。
 * React の `setState` 関数形式の更新関数からそのまま呼び出せる形にして
 * あり、DOM や React が無い bun test でも純関数として直接検証できる。
 *
 * - 位置未確定 (`prev` が null、ドラッグ未実施でCSSのtop/right指定に
 *   任せている状態) は no-op でそのまま null を返す。
 * - クランプ結果が実質同じ値なら `prev` への参照をそのまま返す。React は
 *   `setState` 関数形式の戻り値が直前の state と同一参照 (`Object.is`)
 *   の場合、再レンダーをスキップするため、これによりパネルを動かして
 *   いない resize では無駄な再レンダーが発生しない。
 */
export function reclampPosition(
  prev: PanelPosition | null,
  panelWidth: number,
  viewportWidth: number,
  viewportHeight: number,
  collapsed: boolean,
): PanelPosition | null {
  if (!prev) return prev;
  const next = clampPanelPosition(
    prev.left,
    prev.top,
    panelWidth,
    viewportWidth,
    viewportHeight,
    collapsed,
  );
  return positionsEqual(next, prev) ? prev : next;
}

/**
 * 保存されたレイアウトを、現在のビューポートへ収めた形で復元する。
 *
 * 復元はパネルが `display: none` の間にも走りうるため実測値を使えない。
 * CSS から最小寸法を読めないぶん、フォールバックの最小寸法を使って
 * 既存の clamp をそのまま通す。過大な値はパネル側の CSS
 * (`max-width` / `max-height`) でも二重に押さえられる。
 */
export function restorePanelLayout(
  layout: DesktopPanelLayout,
  viewportWidth: number,
  viewportHeight: number,
  collapsed: boolean,
  fallbackMinSize: PanelSize = {
    width: FALLBACK_MIN_PANEL_WIDTH_PX,
    height: FALLBACK_MIN_PANEL_HEIGHT_PX,
  },
): PanelRect {
  const size = clampPanelSize(
    { width: layout.width, height: layout.height },
    {
      minWidth: fallbackMinSize.width,
      minHeight: fallbackMinSize.height,
      maxWidth: viewportWidth,
      maxHeight: viewportHeight,
    },
  );
  const position = clampPanelPosition(
    layout.left,
    layout.top,
    size.width,
    viewportWidth,
    viewportHeight,
    collapsed,
  );
  return { ...position, ...size };
}

/**
 * 保存済みレイアウトを読み出して復元する。保存キー未指定、保存値なし、
 * window が無い環境 (SSR / DOM無しテスト) ではいずれも null を返し、
 * 従来どおり CSS の既定位置・既定サイズに任せる。
 */
function restoreSavedLayout(
  persistKey: DesktopPanelLayoutKey | undefined,
  collapsed: boolean,
  fallbackMinSize?: PanelSize,
): PanelRect | null {
  if (!persistKey) return null;
  const viewport = globalThis.window;
  if (!viewport) return null;
  const stored = loadDesktopPanelLayout(persistKey);
  if (!stored) return null;
  return restorePanelLayout(
    stored,
    viewport.innerWidth,
    viewport.innerHeight,
    collapsed,
    fallbackMinSize,
  );
}

/** subscribeToResize が要求する最小限のイベントターゲット形状。 */
interface ResizeEventTarget {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

/**
 * window の resize イベント購読/解除を切り出したもの。DOM 依存部分
 * (`addEventListener`/`removeEventListener`) をインターフェースとして
 * 引数化しているため、実ブラウザ/jsdom が無い bun test でも
 * `{ addEventListener, removeEventListener }` を実装した最小限のフェイク
 * を渡すだけでリスナー登録・解除の対称性を検証できる。
 */
export function subscribeToResize(
  target: ResizeEventTarget,
  onResize: () => void,
): () => void {
  target.addEventListener("resize", onResize);
  return (): void => target.removeEventListener("resize", onResize);
}

/**
 * PC版のフローティングフォームを移動・サイズ変更できるようにする。
 * 初期位置はCSS (top/right) のまま、移動した時点で left/top 指定へ切り替える。
 * サイズ変更は左上・左下・右下の隅ハンドルのドラッグと矢印キーで行う。
 * `persistKey` を渡すと、位置とサイズをブラウザーへ保存して次回も再現する。
 */
export function useDraggablePanel(
  options: UseDraggablePanelOptions = {},
): UseDraggablePanelResult {
  const {
    collapsed = false,
    persistKey,
    fallbackMinSize,
    restoreOnPersistKeyChange = false,
    transientHeight = 0,
  } = options;
  const panelRef = useRef<HTMLElement | null>(null);
  // 保存値の復元は初回描画に間に合わせる必要がある。useEffect だと
  // 既定サイズで一度描画されてから保存サイズへ飛ぶちらつきが出る。
  const [restored] = useState<PanelRect | null>(() =>
    restoreSavedLayout(persistKey, collapsed, fallbackMinSize),
  );
  const [position, setPosition] = useState<PanelPosition | null>(
    restored ? { left: restored.left, top: restored.top } : null,
  );
  const [size, setSize] = useState<PanelSize | null>(
    restored ? { width: restored.width, height: restored.height } : null,
  );
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseLeft: number;
    baseTop: number;
    panelWidth: number;
    panelHeight: number;
  } | null>(null);
  /**
   * 進行中のポインター操作で最後に適用した位置と基本寸法（展開分を除く）。
   * 操作の確定時にこれをそのまま保存することで、直前の state 更新が
   * DOM へ反映される前に計測してしまう取りこぼしを避ける。
   * 動かさずに離しただけの操作では null のままで、保存も行わない。
   */
  const pendingRectRef = useRef<PanelRect | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    corner: PanelResizeCorner;
    startX: number;
    startY: number;
    baseRect: PanelRect;
    constraints: PanelSizeConstraints;
  } | null>(null);

  // resize イベントリスナーは mount 時に一度だけ登録するため、ハンドラー内で
  // 常に最新の collapsed を参照できるよう ref に同期しておく。
  const collapsedRef = useRef(collapsed);
  const activePersistKeyRef = useRef(persistKey);

  useLayoutEffect(() => {
    if (
      !restoreOnPersistKeyChange ||
      activePersistKeyRef.current === persistKey
    ) {
      activePersistKeyRef.current = persistKey;
      return;
    }

    activePersistKeyRef.current = persistKey;
    dragStateRef.current = null;
    resizeStateRef.current = null;
    pendingRectRef.current = null;

    const nextLayout = restoreSavedLayout(
      persistKey,
      collapsed,
      fallbackMinSize,
    );
    setPosition(
      nextLayout ? { left: nextLayout.left, top: nextLayout.top } : null,
    );
    setSize(
      nextLayout
        ? { width: nextLayout.width, height: nextLayout.height }
        : null,
    );
  }, [collapsed, fallbackMinSize, persistKey, restoreOnPersistKeyChange]);

  // collapsed が変化した (折りたたみトグルが押された) 直後に再クランプする。
  // ドラッグは非折りたたみ時のタイトルバーからしか開始できないため、画面
  // 右端寄りの位置で開いた状態からトグルで折りたたむと、タブが画面外へ
  // 出て掴めなくなる (Issue #151 レビュー指摘: resize の再クランプだけでは
  // このケースを救えない)。
  useEffect(() => {
    collapsedRef.current = collapsed;
    const panel = panelRef.current;
    if (!panel) return;
    setPosition((prev) =>
      reclampPosition(
        prev,
        panel.offsetWidth,
        window.innerWidth,
        window.innerHeight,
        collapsed,
      ),
    );
  }, [collapsed]);

  // ドラッグ後に一度でも position が確定すると、以後は固定px座標のまま
  // ウィンドウのリサイズに追従しない (Issue #151)。ドラッグ後にウィンドウを
  // 縮めると、パネル (開閉トグルの「タブ」を含む) がビューポート外へ出て
  // 二度と掴めなくなる不具合になるため、position確定後はリサイズのたびに
  // 同じ clampPanelPosition で再クランプする。ドラッグ未実施 (position=null)
  // の間はCSSのtop/right指定（ビューポート相対で常に安全）のままにする。
  //
  // resize は短時間に連続発火しうるため rAF で1フレームに1回へ間引き、
  // かつクランプ結果が前回と同じ (left/top とも同値) なら setState を
  // スキップして無駄な再レンダーを避ける (clampPanelPosition は毎回
  // 新しいオブジェクトを返すため、値の比較を挟まないとパネルを動かして
  // いなくても resize のたびに常に再レンダーが走ってしまう)。
  useEffect(() => {
    let pendingFrame: number | null = null;
    const handleResize = (): void => {
      if (pendingFrame !== null) return;
      pendingFrame = window.requestAnimationFrame(() => {
        pendingFrame = null;
        const panel = panelRef.current;
        if (!panel) return;
        // collapse中はCSSの transform (translateX) で見た目だけ右へ
        // ずれるため、offsetWidth (transformの影響を受けない実寸) を使う。
        setPosition((prev) =>
          reclampPosition(
            prev,
            panel.offsetWidth,
            window.innerWidth,
            window.innerHeight,
            collapsedRef.current,
          ),
        );
      });
    };
    const unsubscribe = subscribeToResize(window, handleResize);
    return (): void => {
      unsubscribe();
      if (pendingFrame !== null) window.cancelAnimationFrame(pendingFrame);
    };
  }, []);

  const readPanelRect = (panel: HTMLElement): PanelRect => {
    const rect = panel.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  };

  const persistLayout = (rect: PanelRect): void => {
    if (!persistKey) return;
    saveDesktopPanelLayout(persistKey, rect);
  };

  /**
   * ポインター操作の確定時に一度だけ保存する。pointermoveごとに書くと
   * フレーム毎の同期書き込みになり操作がカクつく。
   */
  const commitPendingLayout = (): void => {
    const pending = pendingRectRef.current;
    pendingRectRef.current = null;
    if (pending) persistLayout(pending);
  };

  const readBaseHeight = (panel: HTMLElement): number => {
    if (size) return size.height;
    if (transientHeight === 0) return panel.offsetHeight;
    // 未保存のCSS既定サイズも、展開分がmax-heightで切られる前の高さから求める。
    // 同期的に計測して復元するため、上限を外した状態は描画されない。
    const maxHeight = panel.style.getPropertyValue("max-height");
    const priority = panel.style.getPropertyPriority("max-height");
    try {
      panel.style.setProperty("max-height", "none", "important");
      return Math.max(0, panel.offsetHeight - transientHeight);
    } finally {
      if (maxHeight) panel.style.setProperty("max-height", maxHeight, priority);
      else panel.style.removeProperty("max-height");
    }
  };

  const onPointerDown = (event: React.PointerEvent<HTMLElement>): void => {
    if (event.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseLeft: rect.left,
      baseTop: rect.top,
      // resize ハンドラーと同じ offsetWidth (transformの影響を受けない実寸)
      // に揃える。transform は translate のみで幅自体は変化しないため現状は
      // rect.width と同値だが、幅の測り方をひとつに統一しておく。
      panelWidth: panel.offsetWidth,
      // max-heightで展開分が切られていても、移動操作は既存の基本高さを保持する。
      panelHeight: readBaseHeight(panel),
    };
    pendingRectRef.current = null;
    event.currentTarget.setPointerCapture(event.pointerId);
    // ドラッグ中のテキスト選択を防ぐ
    event.preventDefault();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const nextPosition = clampPanelPosition(
      dragState.baseLeft + event.clientX - dragState.startX,
      dragState.baseTop + event.clientY - dragState.startY,
      dragState.panelWidth,
      window.innerWidth,
      window.innerHeight,
      collapsed,
    );
    setPosition(nextPosition);
    // 移動では寸法が変わらないため、掴んだ時点の基本寸法をそのまま持ち回る。
    pendingRectRef.current = {
      ...nextPosition,
      width: dragState.panelWidth,
      height: dragState.panelHeight,
    };
  };

  const endDrag = (event: React.PointerEvent<HTMLElement>): void => {
    if (dragStateRef.current?.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    commitPendingLayout();
  };

  const getResizeConstraints = (
    panel: HTMLElement,
    corner: PanelResizeCorner,
    baseRect: PanelRect,
  ): PanelSizeConstraints => {
    const computedStyle = window.getComputedStyle(panel);
    const parsedMinWidth = Number.parseFloat(computedStyle.minWidth);
    const parsedMinHeight = Number.parseFloat(computedStyle.minHeight);

    return {
      minWidth: Number.isFinite(parsedMinWidth)
        ? parsedMinWidth
        : (fallbackMinSize?.width ?? FALLBACK_MIN_PANEL_WIDTH_PX),
      minHeight: Number.isFinite(parsedMinHeight)
        ? parsedMinHeight
        : (fallbackMinSize?.height ?? FALLBACK_MIN_PANEL_HEIGHT_PX) +
          transientHeight,
      ...getResizeMaxBounds(
        corner,
        baseRect,
        window.innerWidth,
        window.innerHeight,
      ),
    };
  };

  /**
   * サイズ変更の結果を反映する。
   *
   * `setPosition` は `clampPanelPosition` を通さない。あれは「掴み直せる
   * 40pxだけ残っていれば画面外でも可」という移動用の緩い規則で、固定辺を
   * 保つというサイズ変更側の保証と競合する。位置は `resolveResizedRect` が
   * 固定辺から逆算した値をそのまま使う。
   */
  const applyResizedRect = (rect: PanelRect): void => {
    setPosition({ left: rect.left, top: rect.top });
    const nextSize = {
      width: rect.width,
      height: Math.max(0, rect.height - transientHeight),
    };
    setSize(nextSize);
    pendingRectRef.current = { ...rect, ...nextSize };
  };

  const onResizePointerDown = (
    corner: PanelResizeCorner,
    event: React.PointerEvent<HTMLElement>,
  ): void => {
    if (event.button !== 0) return;
    // 別の隅を掴んだままの多点タッチで状態が上書きされないようにする。
    if (resizeStateRef.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    const baseRect = readPanelRect(panel);

    // right配置のままだと右側の隅の移動方向と幅の増減が逆になるため、
    // サイズ変更開始時に現在位置をleft/topへ固定する。
    setPosition({ left: baseRect.left, top: baseRect.top });
    resizeStateRef.current = {
      pointerId: event.pointerId,
      corner,
      startX: event.clientX,
      startY: event.clientY,
      baseRect,
      constraints: getResizeConstraints(panel, corner, baseRect),
    };
    pendingRectRef.current = null;
    event.currentTarget.setPointerCapture(event.pointerId);
    event.stopPropagation();
    event.preventDefault();
  };

  const onResizePointerMove = (
    event: React.PointerEvent<HTMLElement>,
  ): void => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;

    applyResizedRect(
      resolveResizedRect(
        resizeState.corner,
        resizeState.baseRect,
        event.clientX - resizeState.startX,
        event.clientY - resizeState.startY,
        resizeState.constraints,
      ),
    );
  };

  const endResize = (event: React.PointerEvent<HTMLElement>): void => {
    if (resizeStateRef.current?.pointerId !== event.pointerId) return;
    resizeStateRef.current = null;
    commitPendingLayout();
  };

  const resizePanelFromCorner = (
    corner: PanelResizeCorner,
    deltaX: number,
    deltaY: number,
  ): void => {
    const panel = panelRef.current;
    if (!panel) return;
    const baseRect = readPanelRect(panel);
    const next = resolveResizedRect(
      corner,
      baseRect,
      deltaX,
      deltaY,
      getResizeConstraints(panel, corner, baseRect),
    );
    applyResizedRect(next);
    // キーボード操作は1打鍵ずつが確定操作なので、その場で保存する。
    commitPendingLayout();
  };

  const shiftPanelX = (deltaX: number): void => {
    if (deltaX === 0) return;
    setPosition((prev) =>
      prev ? { left: prev.left + deltaX, top: prev.top } : prev,
    );
  };

  const resetPanelLayout = (): void => {
    setSize(null);
    setPosition(null);
    pendingRectRef.current = null;
    // 保存値を消さないと、既定へ戻したつもりでもリロードで元の大きさに戻る。
    if (persistKey) clearDesktopPanelLayout(persistKey);
  };

  const onResizeKeyDown = (
    corner: PanelResizeCorner,
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    if (event.key === "Home") {
      event.preventDefault();
      resetPanelLayout();
      return;
    }
    const delta = resolveResizeKeyboardDelta(event.key, event.shiftKey);
    if (!delta) return;
    event.preventDefault();
    resizePanelFromCorner(corner, delta.deltaX, delta.deltaY);
  };

  const createCornerHandlers = (
    corner: PanelResizeCorner,
  ): ResizeHandleProps => ({
    onPointerDown: (event: React.PointerEvent<HTMLElement>): void =>
      onResizePointerDown(corner, event),
    onPointerMove: onResizePointerMove,
    onPointerUp: endResize,
    onPointerCancel: endResize,
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>): void =>
      onResizeKeyDown(corner, event),
  });

  return {
    panelRef,
    panelStyle:
      position || size
        ? {
            ...(position
              ? { left: position.left, top: position.top, right: "auto" }
              : {}),
            ...(size
              ? { width: size.width, height: size.height + transientHeight }
              : {}),
          }
        : undefined,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
    resizeHandleProps: {
      "top-left": createCornerHandlers("top-left"),
      "bottom-left": createCornerHandlers("bottom-left"),
      "bottom-right": createCornerHandlers("bottom-right"),
    },
    shiftPanelX,
  };
}
